/**
 * @fileoverview RoomContextMenu Component - Right-click context menu for floor plan rooms
 * @author MCHD Room Scheduler Team
 * @created 09-10-2025 04:30:44
 * 
 * Context menu triggered by right-clicking a room on the floor plan canvas.
 * Per Floor Plan Spec Section 3: Provides quick actions aligned with coordinator workflows.
 * 
 * Menu Items (in order):
 * 1. Assign doctor → Opens AssignModal
 * 2. Create block → Opens BlockModal
 * 3. View schedule → Opens RoomDetailSidebar Schedule tab
 * 4. Open in Planner Grid → Navigates to Grid view with room filter
 * 
 * Disabled states:
 * - Assign doctor: Disabled if room is Inactive or Blocked
 * - Create block: Never disabled
 * - View schedule: Never disabled
 * - Open in Grid: Never disabled
 */

import React from 'react';
import {
  Menu,
  Text,
  Divider
} from '@mantine/core';
import {
  IconUserPlus,
  IconBan,
  IconCalendar,
  IconGrid3x3
} from '@tabler/icons-react';

// =============================================================================
// TYPES
// =============================================================================

export interface RoomForContextMenu {
  id: string;
  number: string;
  name: string;
  isActive: boolean;
  blocks: Array<{
    id: string;
    startDate: string;
    endDate: string;
  }>;
}

export type ContextMenuAction = 
  | 'assign-doctor'
  | 'create-block'
  | 'view-schedule'
  | 'open-in-grid';

export interface RoomContextMenuProps {
  /** Room data for context */
  room: RoomForContextMenu | null;
  
  /** Menu position */
  position: { x: number; y: number } | null;
  
  /** Currently selected date (for block overlap check) */
  selectedDate?: Date | undefined;
  
  /** Menu is opened */
  opened: boolean;
  
  /** Close menu callback */
  onClose: () => void;
  
  /** Action selected callback */
  onAction: (action: ContextMenuAction, room: RoomForContextMenu) => void;
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Check if room is blocked for selected date
 * @param room - Room data
 * @param selectedDate - Date to check
 * @returns True if blocked
 */
function isRoomBlocked(room: RoomForContextMenu, selectedDate?: Date): boolean {
  if (!selectedDate || room.blocks.length === 0) return false;
  
  return room.blocks.some(block => {
    const startDate = new Date(block.startDate);
    const endDate = new Date(block.endDate);
    return selectedDate >= startDate && selectedDate <= endDate;
  });
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * RoomContextMenu Component
 * 
 * Right-click context menu for floor plan room interactions.
 * Menu is positioned at cursor coordinates using Mantine Menu with controlled state.
 * 
 * Disabled state logic per Spec Section 3:
 * - Assign doctor disabled if: room.isActive === false OR room is blocked for selectedDate
 * - Other actions: Always enabled
 * 
 * Tooltips explain why actions are disabled.
 * 
 * @example
 * ```tsx
 * <RoomContextMenu
 *   room={selectedRoom}
 *   position={{ x: 150, y: 200 }}
 *   opened={contextMenuOpen}
 *   onClose={() => setContextMenuOpen(false)}
 *   onAction={(action, room) => {
 *     switch (action) {
 *       case 'assign-doctor': openAssignModal(room); break;
 *       case 'create-block': openBlockModal(room); break;
 *       // ...
 *     }
 *   }}
 * />
 * ```
 */
export const RoomContextMenu: React.FC<RoomContextMenuProps> = ({
  room,
  position,
  selectedDate,
  opened,
  onClose,
  onAction
}) => {
  if (!room || !position) return null;
  
  // Check if room can be assigned (not inactive, not blocked)
  const isBlocked = isRoomBlocked(room, selectedDate);
  const canAssign = room.isActive && !isBlocked;
  
  // Get disabled reason for tooltip
  const getAssignDisabledReason = (): string => {
    if (!room.isActive) return 'Room is inactive';
    if (isBlocked) return 'Room is blocked for selected date';
    return '';
  };
  
  return (
    <Menu
      opened={opened}
      onClose={onClose}
      withinPortal
      shadow="md"
      width={220}
      position="right-start"
      offset={2}
      styles={{
        dropdown: {
          position: 'fixed',
          left: position.x,
          top: position.y
        }
      }}
    >
      <Menu.Target>
        {/* Hidden target - menu positioned manually */}
        <div style={{ position: 'absolute', left: position.x, top: position.y }} />
      </Menu.Target>
      
      <Menu.Dropdown>
        {/* Room info header */}
        <Menu.Label>
          <Text size="xs" fw={600} c="dimmed">
            Room {room.number}
          </Text>
          <Text size="xs" c="dimmed" lineClamp={1}>
            {room.name}
          </Text>
        </Menu.Label>
        
        <Divider my={4} />
        
        {/* Assign Doctor */}
        <Menu.Item
          leftSection={<IconUserPlus size={16} />}
          disabled={!canAssign}
          onClick={() => {
            if (canAssign) {
              onAction('assign-doctor', room);
              onClose();
            }
          }}
          style={{
            cursor: canAssign ? 'pointer' : 'not-allowed'
          }}
          title={!canAssign ? getAssignDisabledReason() : 'Open assignment modal'}
        >
          Assign Doctor
        </Menu.Item>
        
        {/* Create Block */}
        <Menu.Item
          leftSection={<IconBan size={16} />}
          onClick={() => {
            onAction('create-block', room);
            onClose();
          }}
        >
          Create Block
        </Menu.Item>
        
        <Divider my={4} />
        
        {/* View Schedule */}
        <Menu.Item
          leftSection={<IconCalendar size={16} />}
          onClick={() => {
            onAction('view-schedule', room);
            onClose();
          }}
        >
          View Schedule
        </Menu.Item>
        
        {/* Open in Planner Grid */}
        <Menu.Item
          leftSection={<IconGrid3x3 size={16} />}
          onClick={() => {
            onAction('open-in-grid', room);
            onClose();
          }}
        >
          Open in Planner Grid
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};

export default RoomContextMenu;

